import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, Package, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const VersionTrends = () => {
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/trm/products`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setProductsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading version trends...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;
  if (!productsData) return null;

  const products = productsData.products || [];
  
  // Sort by version count
  const sortedByVersions = [...products].sort((a, b) => (b.version_count || 0) - (a.version_count || 0));
  const topVersioned = sortedByVersions.slice(0, 15);
  
  // Calculate stats
  const totalVersions = products.reduce((sum, p) => sum + (p.version_count || 0), 0);
  const avgVersions = (totalVersions / products.length).toFixed(1);
  const maxVersions = Math.max(...products.map(p => p.version_count || 0));
  const highComplexity = products.filter(p => (p.version_count || 0) > avgVersions * 1.5).length;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          Version Trends & Complexity
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Analyze version proliferation and identify complexity hotspots
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div style={{ background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)' }}>
          <Activity size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{totalVersions}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Versions Tracked</div>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, var(--cares-magenta) 0%, var(--cares-magenta) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)', boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)' }}>
          <TrendingUp size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{avgVersions}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Average Versions/Product</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-teal) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)', boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)' }}>
          <Package size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{maxVersions}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Max Versions (Single Product)</div>
        </div>

        <div style={{ background: 'linear-gradient(135deg, var(--cares-magenta) 0%, color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card)) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)', boxShadow: '0 4px 12px rgba(250, 112, 154, 0.3)' }}>
          <AlertCircle size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{highComplexity}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>High Complexity Products</div>
        </div>
      </div>

      {/* Top Versioned Products Chart */}
      <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '24px' }}>
          Top 15 Products by Version Count
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {topVersioned.map((product, idx) => {
            const percentage = ((product.version_count / maxVersions) * 100);
            const isHigh = product.version_count > avgVersions * 1.5;
            const color = isHigh ? '#ef4444' : product.version_count > avgVersions ? '#f59e0b' : '#10b981';
            
            return (
              <div key={product.product_id} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ minWidth: '24px', fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-label)', textAlign: 'right' }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>{product.canonical_name}</span>
                      <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginLeft: '12px' }}>
                        {product.manufacturer_name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isHigh && <AlertCircle size={16} color="var(--cares-orange)" />}
                      <span style={{ fontSize: '14px', fontWeight: '700', color: color }}>
                        {product.version_count} versions
                      </span>
                    </div>
                  </div>
                  <div style={{ height: '8px', background: 'var(--cares-surface-board)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                      transition: 'width 0.5s ease',
                      boxShadow: `0 0 8px ${color}44`
                    }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Version Distribution Analysis */}
      <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '20px' }}>
          Version Distribution Analysis
        </h2>
        
        {/* Distribution buckets */}
        {(() => {
          const buckets = [
            { label: '1-10 versions', min: 1, max: 10, color: 'var(--cares-green)' },
            { label: '11-20 versions', min: 11, max: 20, color: 'var(--cares-blue)' },
            { label: '21-30 versions', min: 21, max: 30, color: 'var(--cares-amber)' },
            { label: '31+ versions', min: 31, max: Infinity, color: 'var(--cares-orange)' }
          ];
          
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {buckets.map(bucket => {
                const count = products.filter(p => {
                  const vc = p.version_count || 0;
                  return vc >= bucket.min && vc <= bucket.max;
                }).length;
                const percentage = ((count / products.length) * 100).toFixed(1);
                
                return (
                  <div key={bucket.label} style={{
                    padding: '20px',
                    borderRadius: '8px',
                    border: `2px solid ${bucket.color}`,
                    background: `${bucket.color}11`
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      {bucket.label}
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: bucket.color, marginBottom: '4px' }}>
                      {count}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
                      {percentage}% of products
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default VersionTrends;
